import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import {
  Button, Col, DatePicker, Input, message, Modal, Row, Form, Select, Alert,
} from 'antd';
import moment from 'moment';
import './ServiceStatus.scss';
import ServiceStatusComponent from '../sharedComponents/serviceStatusComponent/ServiceStatusComponent';
import { getUserType } from '../../utils/users';
import {
  get, put, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../dataSource/requests';
import UploadFiles from '../service/serviceComponents/uploadFiles/UploadFiles';
import { SessionExpiredException } from '../../utils/errors';

const ServiceStatus = (): JSX.Element => {
  const navigate = useNavigate();
  const routeMatch = useMatch('/service/status/:id');
  const [scheduleFormInstance] = Form.useForm();
  const [cancelFormInstance] = Form.useForm();
  const [formInstance] = Form.useForm();
  let uploadFilesReference = null;

  const [userType] = useState<string | null>(getUserType());
  const [visibleModalNext, setVisibleModalNext] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [visibleModalCancel, setVisibleModalCancel] = useState<boolean>(false);
  const [visibleModalUpload, setVisibleModalUpload] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [statusesList, setStatusesList] = useState([]);
  const [confirmList] = useState([{ value: 'Não', id: 1 }, { value: 'Sim', id: 2 }]);
  const [currentStatusIndex, setCurrentStatusIndex] = useState<number>(0);
  const [canceled, setCanceled] = useState<boolean>(false);
  const [initialFiles, setInitialFiles] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const uploadAttachmentReports = useRef(null);
  const valueAttachmentSizeCoopercitrus = useRef(null);
  const [dataAttachmentsReports, setDataAttachmentsReports] = useState([]);

  const getValueSystemParameters = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const getSystemParametersReqRef: RequestWrapper = get({
        url: '/system/getSystemParameters',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await getSystemParametersReqRef;

      valueAttachmentSizeCoopercitrus.current = response?.body
        .getSystemParametersOutput
        .attachmentSizeCoopercitrus;
    } catch (error) {
      message.error('Ocorreu um erro ao buscar informações, tente novamente');
    }
  };

  const validateScheduleDate = (rule, value): Promise<void> => {
    if (!value) {
      return Promise.reject(new Error('Por favor, insira data de agendamento.'));
    }

    const dateFormat = 'DD/MM/YYYY';

    if (moment(value).endOf('day') < moment().endOf('day')) {
      return Promise.reject(new Error(`Insira uma data maior ou igual a ${moment().format(dateFormat)}`));
    }

    return Promise.resolve();
  };

  const loadStatusData = async (): Promise<void> => {
    setLoadingStatus(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const getServiceStatusFlowReqRef: RequestWrapper = get({
        url: `/service/getServiceStatusFlow/${routeMatch?.params.id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await getServiceStatusFlowReqRef;
    } catch (error) {
      console.log(error);

      if (!(error instanceof SessionExpiredException)) {
        message.error('Ocorreu um erro ao buscar os detalhes, tente novamente');
      }

      setLoadingStatus(false);
      return;
    }

    if (!response?.body.error) {
      const newStatusesList = response?.body.serviceStatusFlowOutputs;
      setStatusesList(newStatusesList);
      let nextId;

      const findLastIndex = (array): number => {
        let finalIndex = 0;
        for (const ind in array) {
          if (array[ind].date != null) {
            finalIndex = ind;
          }
        }
        if (Number.parseInt(finalIndex, 10) + 1 === array.length) {
          nextId = null;
        } else {
          nextId = array[Number.parseInt(finalIndex, 10) + 1].id;
        }
        return finalIndex;
      };

      const statIndex = findLastIndex(newStatusesList);
      setCurrentStatusIndex(Number.parseInt(statIndex, 10));
      formInstance.setFieldsValue({
        selectForm: nextId,
      });

      setSelectedStatus(
        newStatusesList[Number.parseInt(statIndex, 10) + 1] !== undefined
          ? newStatusesList[Number.parseInt(statIndex, 10) + 1]
          : newStatusesList[Number.parseInt(statIndex, 10)],
      );

      setCanceled(!!newStatusesList.find((v) => v.reasonForCancellation));
      setLoadingStatus(false);
    } else {
      setLoadingStatus(false);
      message.error('Ocorreu um erro ao buscar os detalhes, tente novamente');
    }
  };

  const handleOkModalAttachmentReport = async (stepId): Promise<void> => {
    let responseReport: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;
    let existReport = false;

    // caso exista arquivo para ser enviado, dispara o envio
    const uploadErrorAttachmentFiles = uploadAttachmentReports.current !== null ? !uploadAttachmentReports.current.validateFiles() : true;
    if (!uploadErrorAttachmentFiles) {
      try {
        await uploadAttachmentReports.current?.handleSubmitFiles();
      } catch (error) {
        if (!(error instanceof SessionExpiredException)) {
          message.error('Erro ao anexar arquivo.');
        }
      }
    } else {
      message.error('Erro ao validar arquivo.');
      return;
    }

    // verifica se existe arquivos para o registro que está atualizando o status
    try {
      const findServiceRequestReqRef: RequestWrapper = get({
        url: `/service/findServiceRequest/${Number(routeMatch?.params.id)}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      responseReport = await findServiceRequestReqRef;

      const dataReportType = responseReport?.body.details.attachments.filter((i) => { return i.typeUser === 'REPORT_ATTACHMENT'; });
      if (dataReportType.length > 0) {
        existReport = true;
      }
    } catch (error) {
      if (!(error instanceof SessionExpiredException)) {
        message.error('Erro ao verificar anexos.');
      }
    }

    if (existReport === false) {
      message.warning('É necessário ter ao menos um arquivo anexado para essa alteração de status.');
    } else {
      const isLast = statusesList[statusesList.length - 1].id === (stepId);
      let values;
      try {
        values = await scheduleFormInstance.validateFields();
      } catch (error) {
        return;
      }

      const { id } = routeMatch.params;
      const params = { serviceRequestId: id };
      params.nextStep = selectedStatus.id;

      if (values.scheduleDate) {
        params.scheduleDate = values.scheduleDate;
      }

      setLoadingModal(true);

      let response;

      try {
        const updateServiceRequestStepReqRef: RequestWrapper = put({
          url: '/service/updateServiceRequestStep',
          params,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await updateServiceRequestStepReqRef;
      } catch (error) {
        if (!(error instanceof SessionExpiredException)) {
          message.error('Erro ao atualizar o status');
        }

        setLoadingModal(false);
        return;
      }

      if (!response?.body.error) {
        if (isLast) {
          setLoadingModal(false);
          setVisibleModalNext(false);
        } else {
          loadStatusData();
          setLoadingModal(false);
          setVisibleModalNext(false);
        }
        if (values.serviceAttatchment === 'Sim') {
          navigate(`/service/edit/${id}`);
        }
      } else {
        setLoadingModal(false);
        message.error('Erro ao atualizar o status');
      }
    }
  };

  const handleOkModalNext = async (): Promise<void> => {
    const value = formInstance.getFieldValue('selectForm');

    if (value === 18) {
      handleOkModalAttachmentReport(value);
    } else {
      const isLast = statusesList[statusesList.length - 1]?.id === (value);
      let values;
      try {
        values = await scheduleFormInstance.validateFields();
      } catch (error) {
        console.log(error);
        return;
      }

      const { id } = routeMatch.params;
      const params = { serviceRequestId: id };
      params.nextStep = selectedStatus.id;

      if (values.scheduleDate) {
        params.scheduleDate = values.scheduleDate;
      }

      setLoadingModal(true);

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      try {
        const updateServiceRequestStepReqRef: RequestWrapper = put({
          url: '/service/updateServiceRequestStep',
          params,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await updateServiceRequestStepReqRef;
      } catch (error) {
        console.log(error);

        if (!(error instanceof SessionExpiredException)) {
          message.error('Erro ao atualizar o status');
        }

        setLoadingModal(false);
        return;
      }

      if (!response?.body.error) {
        setLoadingModal(false);
        setVisibleModalNext(false);
        loadStatusData();
        if (isLast) {
          loadStatusData();
        }
        if (values.serviceAttatchment === 'Sim') {
          navigate(`/service/edit/${id}`);
        }
      } else {
        setLoadingModal(false);
        message.error('Erro ao atualizar o status');
      }
    }
  };

  const handleOkModalUpload = async (): Promise<boolean> => {
    const uploadError = !uploadFilesReference.validateFiles();

    if (!uploadError) {
      try {
        await uploadFilesReference.handleSubmitFiles();
        loadStatusData();
        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    }

    return false;
  };

  const handleOkModalCancel = async (): Promise<void> => {
    let values;

    try {
      values = await cancelFormInstance.validateFields(['reasonForCancellation']);
    } catch (error) {
      console.log(error);
      return;
    }

    const { id } = routeMatch.params;
    const params = {
      serviceRequestId: id,
      reasonForCancellation: values.reasonForCancellation,
    };

    setLoadingModal(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const cancelServiceRequestReqRef: RequestWrapper = put({
        url: '/service/cancelServiceRequest',
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await cancelServiceRequestReqRef;
    } catch (error) {
      console.log(error);

      if (!(error instanceof SessionExpiredException)) {
        message.error('Erro ao atualizar o status');
      }

      setLoadingModal(false);
      return;
    }

    if (!response?.body.error) {
      loadStatusData();
      setLoadingModal(false);
      setVisibleModalCancel(false);
      navigate(`/service/detail/${id}`);
    } else {
      setLoadingModal(false);
      message.error('Erro ao atualizar o status');
    }
  };

  const onChangeSelect = (): void => {
    const value = formInstance.getFieldValue('selectForm');
    for (const index in statusesList) {
      if (statusesList[index]?.id === value) {
        setSelectedStatus(statusesList[index]);
      }
    }
  };

  const getReports = async (): Promise<void> => {
    let responseReport: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      const findServiceRequestReqRef: RequestWrapper = get({
        url: `/service/findServiceRequest/${Number(routeMatch?.params.id)}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      responseReport = await findServiceRequestReqRef;

      setDataAttachmentsReports(responseReport?.body.details.attachments);
    } catch (error) {
      if (!(error instanceof SessionExpiredException)) {
        message.error('Erro ao verificar anexos.');
      }
    }
  };

  useEffect(() => {
    getValueSystemParameters();
    getReports();

    const loadInitialFiles = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      try {
        const findAllServiceRequestFilesReqRef: RequestWrapper = get({
          url: `/file/findAllServiceRequestFiles/${routeMatch?.params.id}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await findAllServiceRequestFilesReqRef;
      } catch (error) {
        console.log(error);
        if (!(error instanceof SessionExpiredException)) {
          message.error('Ocorreu um erro ao buscar os arquivos anexados, tente novamente');
        }
        return;
      }

      if (!response?.body.error) {
        setInitialFiles(
          response?.body.files.map((item) => ({ ...item, uid: item.id, status: 'done' })),
        );
      } else {
        message.error('Ocorreu um erro ao buscar os arquivos anexados, tente novamente');
      }
    };

    loadStatusData();
    loadInitialFiles();

    return () => {
      setLoadingModal(false);
    };
  }, []);

  return (
    <div className="ServiceStatus">
      <div className="page-title-container">
        {
          userType === 'COOPERCITRUS'
          && (
            <Row gutter={24}>
              <Col xs={{ span: 24 }} lg={{ span: 20 }}>
                <span className="page-title">Status da solicitação</span>
              </Col>
              <Col xs={{ span: 24 }} lg={{ span: 4 }}>
                <div className="buttons-container">
                  <Row gutter={[24, 24]}>
                    <Col xs={{ span: 24 }} md={{ span: 5 }}>
                      <Button
                        className="btn-previous"
                        onClick={
                          (): void => {
                            navigate('/service/list');
                          }
                        }
                      >
                        Voltar
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )
        }
        <Row className="buttons-container">
          <Col className="SelectStatus">
            <Form
              form={formInstance}
              scrollToFirstError
            >
              <Form.Item
                name="selectForm"
                rules={[
                  { required: true, message: 'Por favor, insira um status para mover a solicitação' },
                ]}
              >
                <Select
                  style={{ minWidth: 200, width: '100%' }}
                  className="selectDrop"
                  onChange={onChangeSelect}
                  disabled={canceled}
                >
                  {
                    statusesList.map((v) => (
                      <Select.Option
                        key={v.stepName}
                        value={v.id}
                      >
                        {v.stepName}
                      </Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
            </Form>
          </Col>
          <Col className="SelectStatus">
            <Button
              className="btn-refresh"
              type="primary"
              onClick={
                (): void => {
                  setVisibleModalNext(true);
                }
              }
              disabled={canceled || statusesList[Number.parseInt(currentStatusIndex, 10)] === selectedStatus || (selectedStatus === undefined || selectedStatus === null || (selectedStatus != null && selectedStatus.length === 0))}
            >
              Atualizar
            </Button>
          </Col>
          <Col>
            <Button
              className="btn-cancel"
              onClick={
                (): void => {
                  setVisibleModalCancel(true);
                }
              }
              disabled={canceled}
            >
              Cancelar solicitação
            </Button>
          </Col>
        </Row>
      </div>
      <div className="page-content">
        <ServiceStatusComponent
          loading={loadingStatus}
          statusesList={statusesList}
          currentStatusIndex={currentStatusIndex}
          canceled={canceled}
        />
      </div>

      <Modal
        wrapClassName="ServiceStatus-next-modal"
        title={<h3 style={{ margin: 0 }}>Modificar status da solicitação?</h3>}
        visible={visibleModalNext}
        onOk={handleOkModalNext}
        onCancel={
          (): void => {
            setVisibleModalNext(false);
          }
        }
        confirmLoading={loadingModal}
        footer={[
          <Button
            key="back"
            onClick={
              (): void => {
                setVisibleModalNext(false);
              }
            }
          >
            Cancelar
          </Button>,
          <Button
            type="primary"
            key="submit"
            loading={loadingModal}
            onClick={handleOkModalNext}
          >
            Confirmar
          </Button>,
        ]}
      >
        <Form
          scrollToFirstError
          form={scheduleFormInstance}
        >
          {
            visibleModalNext
            && (
              selectedStatus.id === 3
                ? (
                  <Form.Item
                    label="Data de agendamento"
                    name="scheduleDate"
                    rules={[
                      { validator: validateScheduleDate },
                      { required: true, message: ' ' },
                    ]}
                    initialValue={moment()}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="Data de agendamento"
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                )
                : (
                  <div>
                    <Row justify="start">
                      <Col className="statusAtualModal">
                        Status Atual:
                        <br />
                        {statusesList[currentStatusIndex]?.stepName}
                      </Col>
                      <Col>
                        Atualizar o status para:
                        <br />
                        {selectedStatus.stepName}
                      </Col>
                    </Row>
                  </div>
                )
            )
          }

          {
            selectedStatus.id === 17 && (
              <Form.Item
                name="serviceAttatchment"
                label="Deseja anexar o relatório de serviço?"
                className="attachment attachment-textonly"
              >
                <Select
                  style={{ minWidth: 200, width: '100%' }}
                  className="selectDrop"
                  defaultValue={confirmList[0]?.value}
                >
                  {
                    confirmList.map((v) => (
                      <Select.Option key={v.id} value={v.value}>
                        {v.value}
                      </Select.Option>
                    ))
                  }
                </Select>
              </Form.Item>
            )
          }

          {
            selectedStatus.id === 18 && (
              <div
                className="attachments-container"
              >
                <Row
                  className="param-row"
                >
                  <div
                    className="field-title-text"
                    style={{ marginBottom: 8 }}
                  >
                    Anexos de relatório
                  </div>
                </Row>
                <div
                  className="attachments-items-container"
                >
                  <UploadFiles
                    typeUser="REPORT_ATTACHMENT"
                    ref={uploadAttachmentReports}
                    origin="edit"
                    requestType="SERVICO"
                    id={routeMatch?.params.id}
                    fileSize={valueAttachmentSizeCoopercitrus.current}
                    action="edit"
                    serviceAction="Pós-vendas"
                    initialFiles={
                      dataAttachmentsReports.map((item) => ({ ...item, uid: item.id, status: 'done' }))
                    }
                  />

                  <Alert
                    className="top-alert"
                    message="Após a confirmação da inserção do relatório de serviços o status desse serviço mudará para 'Anexo de Relatório'."
                    type="warning"
                  />
                </div>
              </div>
            )
          }
        </Form>
      </Modal>

      <Modal
        wrapClassName="ServiceStatus-next-modal"
        title={<h3 style={{ margin: 0 }}>Deseja anexar arquivos?</h3>}
        visible={visibleModalUpload}
        onOk={handleOkModalUpload}
        onCancel={
          (): void => {
            loadStatusData();
            setVisibleModalUpload(false);
          }
        }
        confirmLoading={loadingModal}
        footer={[
          <Button
            key="back"
            onClick={
              (): void => {
                loadStatusData();
                setVisibleModalUpload(false);
              }
            }
          >
            Agora não
          </Button>,
          <Button
            type="primary"
            key="submit"
            loading={loadingModal}
            onClick={handleOkModalUpload}
          >
            Salvar anexos
          </Button>,
        ]}
      >
        <div className="attachments-container">
          <UploadFiles
            ref={
              (element): void => {
                uploadFilesReference = element;
              }
            }
            id={Number.parseInt(routeMatch.params.id, 10)}
            action="edit"
            requestType="SERVICO"
            initialFiles={initialFiles}
          />
        </div>
      </Modal>

      <Modal
        wrapClassName="ServiceStatus-cancel-modal"
        title={<h3 style={{ margin: 0 }}>Escreva o motivo do cancelamento</h3>}
        visible={visibleModalCancel}
        onOk={handleOkModalCancel}
        onCancel={
          (): void => {
            setVisibleModalCancel(false);
          }
        }
        confirmLoading={loadingModal}
        footer={[
          <Button
            key="back"
            className="btn-back"
            onClick={
              (): void => {
                setVisibleModalCancel(false);
              }
            }
          >
            Voltar
          </Button>,
          <Button
            type="primary"
            key="submit"
            className="btn-cancel-confirm"
            loading={loadingModal}
            onClick={handleOkModalCancel}
          >
            Confirmar cancelamento
          </Button>,
        ]}
      >
        {
          visibleModalCancel
          && (
            <Form
              scrollToFirstError
              form={cancelFormInstance}
            >
              <Form.Item
                label="Motivo do cancelamento"
                name="reasonForCancellation"
                rules={[
                  { required: true, message: 'Por favor, insira o motivo do cancelamento' },
                  { max: 200, message: 'Máximo de 200 caracteres' },
                ]}
                initialValue=""
              >
                <Input.TextArea placeholder="Motivo do cancelamento" rows={4} />
              </Form.Item>
            </Form>
          )
        }
      </Modal>
    </div>
  );
};

ServiceStatus.propTypes = {};
ServiceStatus.defaultProps = {};

export default ServiceStatus;
